import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import LockIcon from '@mui/icons-material/Lock'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { Alert } from '@mui/material'

import { IRootState } from '../../../redux/reducers'
import {
  ConfigurationState,
  IdentityState,
} from '../../../redux/reducers/initialState'
import { usePermissions } from '../permission/usePermissions'
import { isNavItem, NavItem, NavItemGroup } from '../types'
import { isScreenSizeSmall } from '../utils'

import HeaderWithNavBar from './HeaderWithNavBar'
import HeaderWithNavDropDown from './HeaderWithNavDropDown'

/**
 * ResponsiveHeader component.
 *
 * This component renders a responsive header that switches between a dropdown navigation
 * and a navigation bar based on the screen size. It listens for window resize events to
 * dynamically update the header layout.
 *
 * @returns {JSX.Element} The rendered responsive header component.
 */
const ResponsiveHeader: React.FC = (): JSX.Element => {
  const {
    permissionsForKeysPages,
    permissionsForPermissionsPage,
    permissionsForEntraIdRolesPage,
    isLoginCompleted,
    userPermissionsState,
  } = usePermissions()
  const { t } = useTranslation()
  /*
   * User Story 1392756 - 09/17/2024
   * Added this hardcoded value to show/hide the Usage Permissions or Permissions tabs in the header
   * We need to delete this variable when we need to completely remove the Device Permissions
   * and display only Usage Permissions tabs from the header
   */
  const identity: IdentityState | null = useSelector(
    (state: IRootState) => state.identity,
  )
  const configuration: ConfigurationState | null = useSelector(
    (state: IRootState) => state.configuration,
  )

  const [isSmallScreen, setIsSmallScreen] = useState(isScreenSizeSmall())
  const [hasAppConfigurationError, setHasAppConfigurationError] =
    useState(false)

  const handleResize = () => {
    setIsSmallScreen(isScreenSizeSmall())
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const checkAppConfigurationError = () => {
      if (configuration !== null) {
        setHasAppConfigurationError(!configuration?.clientId)
      }
    }

    checkAppConfigurationError()
  }, [configuration])

  // Navigation items to be displayed in the header
  const iconSx = {
    height: '5vh',
    width: '5vw',
  }

  const localAccessKeysNavItem: NavItem = {
    id: 'localaccesskeys',
    to: '/keys',
    label: t('local_access_keys'),
    requiredPermissions: permissionsForKeysPages,
    icon: <VpnKeyIcon sx={iconSx} />,
  }

  const signingKeysNavItem: NavItem = {
    id: 'signingkeys',
    to: '/signingkeys',
    label: t('signing_keys'),
    requiredPermissions: permissionsForKeysPages,
    icon: <VpnKeyIcon sx={iconSx} />,
  }

  const endorsementKeysNavItem: NavItem = {
    id: 'endorsementkeys',
    to: '/endorsementkeys',
    label: t('endorsement_keys'),
    requiredPermissions: permissionsForKeysPages,
    icon: <VpnKeyIcon sx={iconSx} />,
  }

  const usagePermissionsNavItem: NavItem = {
    id: 'usagepermissions',
    to: '/usagepermissions',
    label: t('usage_permissions'),
    requiredPermissions: permissionsForPermissionsPage,
    icon: <LockIcon sx={iconSx} />,
  }

  const permissionsNavItem: NavItem = {
    id: 'keymappings',
    to: '/keymappings',
    label: t('permissions'),
    requiredPermissions: permissionsForPermissionsPage,
    icon: <LockIcon sx={iconSx} />,
  }

  const entraIDRolesNavItem: NavItem = {
    id: 'entraidroles',
    to: '/entraidroles',
    label: t('entra_id_roles'),
    requiredPermissions: permissionsForEntraIdRolesPage,
    icon: <PersonOutlineIcon sx={iconSx} />,
  }

  const unlockPinNavItem: NavItem = {
    id: 'unlockpin',
    to: '/unlock-pin',
    label: t('unlock_pin'),
    icon: <QrCodeScannerIcon sx={iconSx} />,
  }

  /**
   * Filters the Menu list based on the user permissions.
   * @param {NavItem[]} navItems
   * @return {*}
   */
  const filterMenu = useCallback(
    (navItems: (NavItem | NavItemGroup)[]) => {
      const filterNavItem = (item: NavItem | NavItemGroup): boolean => {
        if (!isNavItem(item)) {
          item.items = filterMenu(item.items)
          return item.items.length > 0
        }
        if (isLoginCompleted) {
          if (item.requiredPermissions) {
            return (
              userPermissionsState !== null &&
              item.requiredPermissions.some(
                (permission) => userPermissionsState[permission] === true,
              )
            )
          }
          return true
        }
        return true
      }

      return navItems.filter(filterNavItem)
    },
    [isLoginCompleted, userPermissionsState],
  )

  const securePlatformManagementKeys: NavItemGroup = useMemo(() => {
    const items = [endorsementKeysNavItem, signingKeysNavItem]

    return {
      id: 'secureplatformmanagementkeys',
      name: t('secure_platform_management_keys'),
      items: filterMenu(items),
      icon: <VpnKeyIcon sx={iconSx} />,
    }
  }, [[signingKeysNavItem, endorsementKeysNavItem, t, filterMenu, iconSx]])

  const manageKeysNavGroup: NavItemGroup = useMemo(() => {
    const items = [securePlatformManagementKeys, localAccessKeysNavItem]

    return {
      id: 'managekeys',
      name: t('key.title'),
      items: filterMenu(items),
      icon: <VpnKeyIcon sx={iconSx} />,
    }
  }, [
    securePlatformManagementKeys,
    localAccessKeysNavItem,
    t,
    filterMenu,
    iconSx,
  ])

  const permittedNavigableItems: (NavItem | NavItemGroup)[] = useMemo(() => {
    const navItems: (NavItem | NavItemGroup)[] = [
      manageKeysNavGroup,
      permissionsNavItem,
      entraIDRolesNavItem,
      unlockPinNavItem,
    ]

    return filterMenu(navItems)
  }, [
    manageKeysNavGroup,
    permissionsNavItem,
    entraIDRolesNavItem,
    unlockPinNavItem,
    filterMenu,
  ])

  return (
    <>
      {hasAppConfigurationError && (
        <Alert variant="filled" severity="error">
          {t('not_configured')}
        </Alert>
      )}
      {isSmallScreen ? (
        <HeaderWithNavDropDown
          navItems={permittedNavigableItems}
          hasAppConfigurationError={hasAppConfigurationError}
        />
      ) : (
        <HeaderWithNavBar
          navItems={permittedNavigableItems}
          hasAppConfigurationError={hasAppConfigurationError}
        />
      )}
    </>
  )
}

export default ResponsiveHeader
